import React, { PropsWithChildren, ReactNode, useMemo } from "react";

import TextField from "@material-ui/core/TextField";
import Autocomplete from "@material-ui/lab/Autocomplete";
import Skeleton from "@material-ui/lab/Skeleton";

import { useSnackbarQuery } from "../../../hooks";

export interface GqlRecordPickerProps extends PropsWithChildren<any> {
  withoutMargin?: boolean;
  isIdBased?: boolean;
  required?: boolean;
  disabled?: boolean;
  multiple: boolean;
  query: any;
  queryResultDataField: string;
  queryVariables?: any;
  getOptionDisabled?: ((option: any) => boolean) | undefined;
  placeHolder: string;
  value: any;
  keyExpr?: string;
  onValueChange: (value: any) => void;
  getOptionLabel: (value: any) => string;
  renderOption: (value: any) => ReactNode;
}

export function GqlRecordPicker(props: GqlRecordPickerProps) {
  const {
    withoutMargin,
    isIdBased,
    disabled,
    multiple,
    required,
    keyExpr = "id",
    query,
    queryResultDataField,
    queryVariables,
    value,
    onValueChange,
    placeHolder,
    getOptionDisabled,
    getOptionLabel,
    renderOption,
  } = props;

  const computedPlaceholder = useMemo(() => {
    return `${placeHolder}${required ? " *" : ""}`;
  }, [placeHolder, required]);

  const { loading, data, error } = useSnackbarQuery(query, {
    variables: queryVariables,
    fetchPolicy: "no-cache",
  });
  const queryData = useMemo(() => {
    if (data && queryResultDataField) {
      return data[queryResultDataField];
    }
    return [];
  }, [data, queryResultDataField]);

  if (loading) {
    return <Skeleton animation="wave" />;
  }
  if (error) {
    return <span>Si è verificato un errore, contattare l'assistenza.</span>;
  }

  return (
    <Autocomplete
      aria-required={required}
      disabled={disabled}
      multiple={multiple}
      value={value}
      onChange={(_evt, newValue) => onValueChange(newValue)}
      options={queryData}
      getOptionSelected={(option, _value) =>
        option[keyExpr] ===
        (typeof _value === "string" && isIdBased ? _value : _value?.[keyExpr])
      }
      getOptionLabel={(value) =>
        getOptionLabel(
          isIdBased && typeof value === "string"
            ? queryData?.find((item) => item.id === value) ?? value
            : value
        )
      }
      getOptionDisabled={getOptionDisabled}
      renderOption={renderOption}
      renderInput={(params) => (
        <TextField
          margin={withoutMargin ? "none" : "dense"}
          {...params}
          style={{ width: "100%" }}
          label={computedPlaceholder}
          variant="outlined"
          inputProps={{
            ...params.inputProps,
            autoComplete: "new-password", // <-- disables autocomplete and automatic fill
          }}
        />
      )}
      style={{
        width: "100%",
      }}
    />
  );
}
